// Synthetic, GTFS-shaped sample data for the in-browser SQL playground.
// NOT the real PMPML feed — it mirrors the table structure of the real project
// (routes / stops / trips / stop_times) so visitors can run realistic queries.
// Deterministic: the same seed always produces the same database.

const mulberry32 = (seed: number) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const pad = (n: number) => String(n).padStart(2, '0');
const clock = (mins: number) => `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}:00`;
const q = (s: string) => `'${s.replace(/'/g, "''")}'`;

const ROUTES = 18;
const STOPS = 90;
const ZONES = ['Central', 'North', 'East', 'West', 'South'];

const insertChunks = (table: string, rows: string[], size = 400) => {
  const out: string[] = [];
  for (let i = 0; i < rows.length; i += size) {
    out.push(`INSERT INTO ${table} VALUES ${rows.slice(i, i + size).join(',')};`);
  }
  return out;
};

export const buildSampleSql = (): string => {
  const rand = mulberry32(20261020);

  const stopRows: string[] = [];
  for (let i = 1; i <= STOPS; i++) {
    const zone = ZONES[Math.floor(rand() * ZONES.length)];
    const lat = (18.45 + rand() * 0.15).toFixed(5);
    const lon = (73.78 + rand() * 0.15).toFixed(5);
    stopRows.push(`(${q('S' + String(i).padStart(3, '0'))},${q(`${zone} Stop ${String(i).padStart(2, '0')}`)},${lat},${lon})`);
  }

  const routeRows: string[] = [];
  const tripRows: string[] = [];
  const stopTimeRows: string[] = [];
  const baseHeadways = [15, 20, 20, 25, 30, 30, 40, 45, 60];
  let tripCounter = 0;

  for (let r = 1; r <= ROUTES; r++) {
    const routeId = `R${pad(r)}`;
    routeRows.push(`(${q(routeId)},${q(String(100 + r * 3))},3)`);

    // pick a distinct, ordered set of stops for this route
    const length = 8 + Math.floor(rand() * 9);
    const pool = Array.from({ length: STOPS }, (_, i) => i + 1);
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    const pattern = pool.slice(0, length).map((n) => 'S' + String(n).padStart(3, '0'));
    const base = baseHeadways[Math.floor(rand() * baseHeadways.length)];

    for (const direction of [0, 1]) {
      const stops = direction === 0 ? pattern : [...pattern].reverse();
      let t = 360 + Math.floor(rand() * base); // first departure after 06:00
      while (t <= 1320) {
        tripCounter += 1;
        const tripId = `T${String(tripCounter).padStart(5, '0')}`;
        tripRows.push(`(${q(tripId)},${q(routeId)},${direction},'WKD')`);

        let clockMins = t;
        stops.forEach((stopId, idx) => {
          if (idx > 0) clockMins += 2 + Math.floor(rand() * 3);
          stopTimeRows.push(`(${q(tripId)},${idx + 1},${q(stopId)},${q(clock(clockMins))},${q(clock(clockMins))})`);
        });

        const peak = (t >= 420 && t < 600) || (t >= 1020 && t < 1200);
        t += peak ? Math.max(8, Math.round(base * 0.6)) : base;
      }
    }
  }

  return [
    'BEGIN;',
    'CREATE TABLE routes (route_id TEXT PRIMARY KEY, route_short_name TEXT, route_type INTEGER);',
    'CREATE TABLE stops (stop_id TEXT PRIMARY KEY, stop_name TEXT, stop_lat REAL, stop_lon REAL);',
    'CREATE TABLE trips (trip_id TEXT PRIMARY KEY, route_id TEXT, direction_id INTEGER, service_id TEXT);',
    'CREATE TABLE stop_times (trip_id TEXT, stop_sequence INTEGER, stop_id TEXT, arrival_time TEXT, departure_time TEXT);',
    'CREATE INDEX idx_st_trip ON stop_times(trip_id);',
    'CREATE INDEX idx_st_stop ON stop_times(stop_id);',
    'CREATE INDEX idx_trips_route ON trips(route_id);',
    ...insertChunks('routes', routeRows),
    ...insertChunks('stops', stopRows),
    ...insertChunks('trips', tripRows),
    ...insertChunks('stop_times', stopTimeRows),
    'COMMIT;',
  ].join('\n');
};

export const sampleSchema: { table: string; columns: string[] }[] = [
  { table: 'routes', columns: ['route_id', 'route_short_name', 'route_type'] },
  { table: 'stops', columns: ['stop_id', 'stop_name', 'stop_lat', 'stop_lon'] },
  { table: 'trips', columns: ['trip_id', 'route_id', 'direction_id', 'service_id'] },
  { table: 'stop_times', columns: ['trip_id', 'stop_sequence', 'stop_id', 'arrival_time', 'departure_time'] },
];

export const presetQueries: { label: string; sql: string }[] = [
  {
    label: 'busiest routes',
    sql: `SELECT r.route_short_name AS route, COUNT(*) AS trips
FROM trips t
JOIN routes r USING (route_id)
GROUP BY r.route_id
ORDER BY trips DESC
LIMIT 10;`,
  },
  {
    label: 'stop coverage',
    sql: `SELECT s.stop_name, COUNT(DISTINCT t.route_id) AS routes_served
FROM stop_times st
JOIN trips t USING (trip_id)
JOIN stops s USING (stop_id)
GROUP BY s.stop_id
ORDER BY routes_served DESC, s.stop_name
LIMIT 10;`,
  },
  {
    label: 'scheduled headway',
    sql: `-- average gap between departures from each route's first stop
WITH first_stop AS (
  SELECT t.route_id, t.direction_id,
         CAST(substr(st.departure_time, 1, 2) AS INT) * 60
       + CAST(substr(st.departure_time, 4, 2) AS INT) AS mins
  FROM stop_times st
  JOIN trips t USING (trip_id)
  WHERE st.stop_sequence = 1
), gaps AS (
  SELECT route_id,
         mins - LAG(mins) OVER (
           PARTITION BY route_id, direction_id ORDER BY mins
         ) AS headway
  FROM first_stop
)
SELECT route_id,
       ROUND(AVG(headway), 1) AS avg_headway_min,
       MAX(headway)           AS worst_gap_min
FROM gaps
WHERE headway IS NOT NULL
GROUP BY route_id
ORDER BY avg_headway_min
LIMIT 10;`,
  },
  {
    label: 'departures by hour',
    sql: `SELECT substr(departure_time, 1, 2) AS hour,
       COUNT(*)                     AS departures
FROM stop_times
WHERE stop_sequence = 1
GROUP BY hour
ORDER BY hour;`,
  },
  {
    label: 'data quality checks',
    sql: `-- the kind of validation the real pipeline runs before anything is analysed
SELECT 'trips without stop_times' AS check_name, COUNT(*) AS failures
FROM trips
WHERE trip_id NOT IN (SELECT trip_id FROM stop_times)
UNION ALL
SELECT 'stop_times with unknown stop', COUNT(*)
FROM stop_times
WHERE stop_id NOT IN (SELECT stop_id FROM stops)
UNION ALL
SELECT 'non-increasing arrival times', COUNT(*)
FROM (
  SELECT arrival_time,
         LAG(arrival_time) OVER (PARTITION BY trip_id ORDER BY stop_sequence) AS prev
  FROM stop_times
)
WHERE prev IS NOT NULL AND arrival_time < prev;`,
  },
];
